import { faCamera } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ArrowLeft, ArrowRight } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import InboxIcon from '@mui/icons-material/Inbox';
import { IconButton } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { PhotoBooth } from '../../../../interfaces/interfaces';
import { fetchAndSetPhotobooths } from '../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../services/helper';
import { createPhotobooth, deletePhotobooth } from '../../../../services/services';
import VyTooltip from '../../../common/VyTooltip';
import VyUpload from '../../../common/VyUpload';
import VyWebcam from '../../../common/VyWebcam';
import { TitleAndDescription } from '../../pageBuilder/TitleAndDescription';
import { UiComponent } from '../../../../interfaces/pagebuilder';

interface PbCell {
  pb: PhotoBooth;
  onClick?: () => void;
  onDelete?: () => void;
  approved?: boolean;
}

const PbCell = ({ pb, onClick, onDelete, approved = true }: PbCell) => {
  const { t } = useTranslation('pagebuilder');
  return (
    <div onClick={onClick} className={`w-full relative overflow-hidden rounded-lg ${onClick && 'cursor-pointer'}`}>
      <img className="w-full h-full" src={createDisplayLink(pb.file)}></img>
      {pb.user && (
        <VyTooltip title={pb.user?.name}>
          <span
            style={{
              width: '-webkit-fill-available',
              background: 'rgba(0, 0, 0, 0.5)',
              minHeight: '1rem',
            }}
            className="absolute top-0 left-0 p-1 font-bold text-center truncate">
            {pb.user?.name}
          </span>
        </VyTooltip>
      )}
      {onDelete && (
        <div className="absolute right-0 bottom-0 z-10 m-1">
          <IconButton onClick={onDelete} size="small" style={{ outline: 'none', background: '#CCCCCC' }}>
            <DeleteIcon />
          </IconButton>
        </div>
      )}
      {!approved && (
        <div className="flex absolute top-0 left-0 justify-center items-center w-full h-full bg-opacity-50 bg-slate-300">
          <span className="font-bold text-center text-transparent text-black text-l">{t('pagebuilder:photobooth.waiting-approval')}</span>
        </div>
      )}
    </div>
  );
};

const ITEMS_PER_PAGE = 9;
const PhotoBoothComponent = ({ component }: { component: UiComponent }) => {
  const [currentPhotobooth, setCurrentPhotobooth] = useState<PhotoBooth | undefined>(undefined);
  const [formattedPhotoBooths, setFormattedPhotoBooths] = useState<PhotoBooth[] | undefined>(undefined);
  const [userPhotoBooth, setUserPhotoBooth] = useState<PhotoBooth | undefined>(undefined);
  const [isWebcam, setIsWebcam] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [maxPageNumber, setMaxPageNumber] = useState<number>(0);

  const dispatch = useDispatch();

  const { t } = useTranslation('pagebuilder');

  const photobooths = useSelector((state) => state.common.photobooths);
  const event = useSelector((state) => state.common.event);
  const user = useSelector((state) => state.common.user);

  useEffect(() => {
    if (event) dispatch(fetchAndSetPhotobooths(event._id));
  }, [event]);

  useEffect(() => {
    let mutable = [...photobooths];
    let myPb: PhotoBooth | undefined = undefined;
    myPb = mutable.find((pb) => pb?.user?._id === user?._id);
    setUserPhotoBooth(myPb);
    mutable = mutable.filter((pb) => pb._id !== myPb?._id && pb.approved);
    const formatted = mutable.slice(ITEMS_PER_PAGE * pageNumber, ITEMS_PER_PAGE + ITEMS_PER_PAGE * pageNumber - 1);
    setFormattedPhotoBooths(formatted);
    // check how many pages we can have for pagination
    setMaxPageNumber(Math.floor(mutable.length / (ITEMS_PER_PAGE - 1)) + 1);
    setCurrentPhotobooth(mutable[0] || myPb);
  }, [photobooths, pageNumber]);

  async function _createPhotobooth(file: string | null) {
    if (file && event) {
      await createPhotobooth({ event: event?._id, file });
      dispatch(fetchAndSetPhotobooths(event._id));
    }
  }
  async function _deletePhotobooth(photobooth: string) {
    if (event && photobooth) {
      await deletePhotobooth(photobooth);
      dispatch(fetchAndSetPhotobooths(event._id));
    }
  }
  const handleNextPage = () => {
    setPageNumber((curr) => curr + 1);
  };
  const handlePreviousPage = () => {
    setPageNumber((curr) => curr - 1);
  };
  return (
    <div className="flex flex-col w-full h-full">
      <TitleAndDescription component={component} />
      <div className="flex h-full w-full flex-col @md:flex-row">
        <div className="flex flex-1 justify-center align-center">
          {!currentPhotobooth ? (
            <div className={`overflow-hidden rounded-lg flex justify-center items-center  flex-col border-2 text-center h-64`}>
              <InboxIcon style={{ color: component.styling.textColor }} />
              <span>{t('pagebuilder:photobooth.no-element')}</span>
            </div>
          ) : (
            <PbCell pb={currentPhotobooth} />
          )}
        </div>
        <div className="flex relative flex-col flex-1 p-4">
          <div className="flex-1">
            <div className={`grid ${(formattedPhotoBooths && formattedPhotoBooths.length > 0) || userPhotoBooth ? 'grid-cols-3' : 'grid-cols-1'} gap-4`}>
              {formattedPhotoBooths &&
                formattedPhotoBooths.length > 0 &&
                formattedPhotoBooths.map((photobooth) => (
                  <div key={photobooth._id} className="w-full">
                    <PbCell pb={photobooth} onClick={() => setCurrentPhotobooth(photobooth)} />
                  </div>
                ))}
              {user && (
                <div className="w-full">
                  {!userPhotoBooth ? (
                    <div
                      style={{ color: component.styling.textColor }}
                      className={`overflow-hidden rounded-lg flex justify-center items-center h-full flex-col border-2 text-center`}>
                      <span className="hidden @lg:flex">{t('pagebuilder:photobooth.add-photo')}</span>
                      <div className={`flex gap-1 ${formattedPhotoBooths ? 'flex-row' : '@lg:flex-row flex-col'}`}>
                        <VyUpload
                          withCrop
                          extended={false}
                          randomExternalImageButton={false}
                          showFileUploadProgress={false}
                          eventId={event?._id}
                          anonymousUpload
                          onChange={(fileId) => _createPhotobooth(fileId)}
                          withMedia={false}></VyUpload>

                        <div
                          onClick={() => setIsWebcam(true)}
                          className="flex justify-center items-center p-2 px-4 my-1 w-full bg-white rounded-full border cursor-pointer outline-none select-none border-slate-300 text-slate-500">
                          <FontAwesomeIcon icon={faCamera} />
                        </div>
                      </div>
                      {isWebcam && (
                        <VyWebcam
                          open={isWebcam}
                          anonymousUserUpload
                          eventid={event?._id}
                          onClose={() => setIsWebcam(false)}
                          onChange={(fileId) => _createPhotobooth(fileId)}
                        />
                      )}
                    </div>
                  ) : (
                    <PbCell
                      pb={userPhotoBooth}
                      approved={userPhotoBooth.approved}
                      onDelete={() => _deletePhotobooth(userPhotoBooth._id)}
                      onClick={() => setCurrentPhotobooth(userPhotoBooth)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-center items-center">
            <IconButton disabled={pageNumber <= 0} size="large">
              <ArrowLeft htmlColor={component.styling.textColor} onClick={handlePreviousPage} />
            </IconButton>
            <span>
              {pageNumber + 1}/{maxPageNumber}
            </span>
            <IconButton disabled={pageNumber + 1 >= maxPageNumber} size="large">
              <ArrowRight htmlColor={component.styling.textColor} onClick={handleNextPage} />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};
export default PhotoBoothComponent;
